import type { FormEvent, JSX } from 'react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateStudent } from '@/features/students/hooks';
import { ApiError } from '@/lib/api/client';

const UUID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * `POST /students` requires an existing registered user's id
 * (`createStudentSchema`) - there is no user-search/lookup endpoint in the
 * current backend, so this form asks for the id directly rather than
 * fabricating a name-based picker the backend cannot serve.
 */
export function CreateStudentDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onCreated: () => void;
}): JSX.Element {
  const [userId, setUserId] = useState('');
  const [guardianName, setGuardianName] = useState('');
  const [guardianPhone, setGuardianPhone] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const create = useCreateStudent();

  function reset(): void {
    setUserId('');
    setGuardianName('');
    setGuardianPhone('');
    setValidationError(null);
    create.reset();
  }

  function handleOpenChange(next: boolean): void {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setValidationError(null);

    if (!UUID_PATTERN.test(userId.trim())) {
      setValidationError('Masukkan ID pengguna yang valid (UUID).');
      return;
    }

    create.mutate(
      {
        userId: userId.trim(),
        guardianName: guardianName.trim().length > 0 ? guardianName.trim() : null,
        guardianPhone: guardianPhone.trim().length > 0 ? guardianPhone.trim() : null,
      },
      {
        onSuccess: () => {
          onCreated();
          handleOpenChange(false);
        },
      },
    );
  }

  const apiErrorMessage =
    create.error instanceof ApiError
      ? create.error.message
      : create.error !== null
        ? 'Terjadi kesalahan. Silakan coba lagi.'
        : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tambah Santri</DialogTitle>
          <DialogDescription>
            Menambahkan profil santri untuk pengguna yang sudah terdaftar di Mahfuz.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="student-user-id">ID Pengguna</Label>
            <Input
              id="student-user-id"
              type="text"
              value={userId}
              onChange={(event) => {
                setUserId(event.target.value);
              }}
              placeholder="00000000-0000-0000-0000-000000000000"
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              ID akun pengguna yang sudah terdaftar dan akan dijadikan santri.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="student-guardian-name">Nama Wali (opsional)</Label>
            <Input
              id="student-guardian-name"
              type="text"
              value={guardianName}
              onChange={(event) => {
                setGuardianName(event.target.value);
              }}
              autoComplete="off"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="student-guardian-phone">Telepon Wali (opsional)</Label>
            <Input
              id="student-guardian-phone"
              type="text"
              value={guardianPhone}
              onChange={(event) => {
                setGuardianPhone(event.target.value);
              }}
              autoComplete="off"
            />
          </div>

          {validationError !== null && (
            <Alert variant="destructive">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
          {apiErrorMessage !== null && (
            <Alert variant="destructive">
              <AlertDescription>{apiErrorMessage}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
